'use client';

import React, { useId, useState } from 'react';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { useTranslations } from 'use-intl';
import { classnames } from '@/utils/classnames/classnames';
import { FilterPillProps } from './types';

// EWM-185 inline filter pill.
//
// Pill-shaped trigger button → opens a popover with a multi-select
// checkbox list. Live updates (no Apply button) per the ticket. When
// any selection is made the trigger gets the primary border, the
// label goes bold, and a count is appended.
//
// Lives in the toolbar above the product grid alongside other pills
// (Brand, Price, etc.). Layout/colours match the v0 mock at
// v0-electus-style-guide.vercel.app/category.
const FilterPill = ({ label, options, value = [], onChange, className = '' }: FilterPillProps) => {
  const translate = useTranslations();
  const listboxId = useId();

  // EWM-372 — inline pills apply live, but `value` is URL-derived and only
  // updates after the SSR round-trip, so the checkbox used to sit unchanged
  // for ~0.5–1s after a click (felt broken). Mirror the selection into local
  // optimistic state so the box + count + active border respond instantly;
  // reconcile to `value` whenever the committed selection arrives (round-trip
  // result, back/forward nav, another tab).
  const [optimistic, setOptimistic] = useState<string[]>(value);
  // EWM-760 — reconcile whenever the committed `value` prop changes
  // (identity), adjusted during render instead of a cascading `useEffect` —
  // same trigger as the old `useEffect(() => …, [value])`, one render sooner.
  const [prevValue, setPrevValue] = useState(value);
  if (prevValue !== value) {
    setPrevValue(value);
    setOptimistic(value);
  }

  const activeCount = optimistic.length;
  const isActive = activeCount > 0;

  const toggle = (optionValue: string) => {
    const next = optimistic.includes(optionValue)
      ? optimistic.filter((v) => v !== optionValue)
      : [...optimistic, optionValue];
    setOptimistic(next);
    onChange?.(next);
  };

  // Screen-reader description of the trigger reflects the current
  // selection state so users hear what's selected before opening the
  // popover. Headless UI handles aria-expanded automatically.
  const triggerLabel = isActive
    ? `${label}, ${activeCount} ${activeCount === 1 ? 'option' : 'options'} selected`
    : `${label}, no selection`;

  return (
    <Popover className={classnames('relative inline-block', className)}>
      {({ open }) => (
        <>
          <PopoverButton
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-label={triggerLabel}
            className={classnames(
              'text-13 inline-flex items-center gap-1.5 rounded-full border bg-white px-4 py-2 text-gray-700 transition-colors',
              'focus:ring-primary/40 focus:ring-2 focus:outline-hidden',
              // EWM-271 — Dan v0 pattern is "open OR active" (per
              // `/tmp/v0-extract/src/app/search/page.tsx:144` and the
              // sibling `/category` mock): the border pulses primary
              // while the popover is open, and stays primary as long
              // as any value is selected.
              open || isActive ? 'border-primary' : 'border-gray-300 hover:border-gray-400',
            )}
          >
            <span aria-hidden>
              {isActive ? (
                <>
                  <strong>{label}</strong> ({activeCount})
                </>
              ) : (
                label
              )}
            </span>
            <ChevronDownIcon className="size-4" aria-hidden />
          </PopoverButton>
          <Transition
            enter="transition ease-out duration-100"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <PopoverPanel className="absolute top-full left-0 z-20 mt-1 w-56 rounded-lg border border-gray-200 bg-white shadow-lg">
              <ul
                id={listboxId}
                className="max-h-80 overflow-auto"
                role="listbox"
                aria-multiselectable
                aria-label={label}
              >
                {options.length === 0 ? (
                  <li className="text-13 px-4 py-2.5 text-gray-500">{translate('product.no-options')}</li>
                ) : (
                  options.map((option) => {
                    const checked = optimistic.includes(option.value);
                    return (
                      <li key={option.value} role="option" aria-selected={checked}>
                        <label className="text-13 flex cursor-pointer items-center gap-3 px-4 py-2.5 hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(option.value)}
                            className="accent-primary size-4 rounded border-gray-300"
                          />
                          <span className={checked ? 'text-primary font-semibold' : 'text-gray-700'}>
                            {option.label}
                          </span>
                        </label>
                      </li>
                    );
                  })
                )}
              </ul>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default FilterPill;
